import { blackOrange, white } from '@/theme/colors'
import { useCheckQrToken } from '@/hooks/useCheckQrToken'
import { useGenerateAuthQrToken } from '@/hooks/useGenerateAuthQrToken'
import { useAuth } from '@context/AuthContext'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import GoogleLoginButton from '../../components/Auth/GoogleLoginButton'
import { trackEvent } from '../../lib/analytics'
import {
	Brand,
	Card,
	Divider,
	Foot,
	GoogleSlot,
	HelpLink,
	Hero,
	HeroTitle,
	Lede,
	LoadingSpinner,
	Main,
	Mark,
	Page,
	QrCodeImage,
	QrRow,
	QrText,
	QrTile,
	SignIn,
	SignInTitle,
	Tagline,
	TopBar,
} from './styles'

const GET_THE_APP_URL = 'https://orbitnow.app'

const LoginPage: React.FC = () => {
	const { setAuth } = useAuth()
	const queryClient = useQueryClient()
	const { data: authQrTokenData, isLoading } = useGenerateAuthQrToken()
	const { data: qrCheckData } = useCheckQrToken(authQrTokenData?.token, !isLoading)

	useEffect(() => {
		if (qrCheckData?.user) {
			// User has authenticated via QR code
			setAuth({ token: qrCheckData.token, user: qrCheckData.user })
			trackEvent('login_success', { method: 'qr' })
			// Reset the query data to prevent auto-login after logout
			queryClient.resetQueries({ queryKey: ['qr-token-check'] })
		}
	}, [qrCheckData, setAuth, queryClient])

	return (
		<Page>
			<TopBar>
				<Brand href={GET_THE_APP_URL} target="_blank" rel="noreferrer">
					<img src="/orbit-icon.svg" alt="Orbit" />
					<b>Orbit</b>
				</Brand>
				<HelpLink href={GET_THE_APP_URL} target="_blank" rel="noreferrer">
					New to Orbit? <b>Get the app</b>
				</HelpLink>
			</TopBar>

			<Main>
				<Card>
					<Hero>
						<HeroTitle>Take your trips out of the group chat.</HeroTitle>
						<Tagline>Plan together, split costs fairly, and actually make the trip happen.</Tagline>

						<Mark>
							<img src="/orbit-icon.svg" alt="" />
							<b>Orbit</b>
						</Mark>
					</Hero>

					<SignIn>
						<SignInTitle>Welcome back</SignInTitle>
						<Lede>Sign in to pick up your itinerary right where you left off.</Lede>

						<GoogleSlot>
							<GoogleLoginButton />
						</GoogleSlot>

						<Divider>or scan to sign in</Divider>

						<QrRow>
							<QrTile>
								{isLoading || !authQrTokenData?.token ? (
									<LoadingSpinner />
								) : (
									<QrCodeImage
										size={92}
										value={`https://orbitnow.app/appLink/qr-auth/${authQrTokenData.token}`}
										viewBox={`0 0 92 92`}
										fgColor={blackOrange}
										bgColor={white}
										level="M"
									/>
								)}
							</QrTile>
							<QrText>
								<b>Already on your phone?</b>
								<p>Open your camera, scan this code, and you&rsquo;re in instantly.</p>
							</QrText>
						</QrRow>

						<Foot>
							New to Orbit?{' '}
							<a href={GET_THE_APP_URL} target="_blank" rel="noreferrer">
								Get the app
							</a>
						</Foot>
					</SignIn>
				</Card>
			</Main>
		</Page>
	)
}

export default LoginPage
